/**
 * Shared CRUD form for uplines and clients. Steps 2 and 3 render this with
 * different role + labels.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import type { Party, PartyRole } from '../api';
import { SectionCard } from '../ui/components';
import { ActionQueueCard } from '../ui/mobileOps';
import {
  Button,
  ErrorBanner,
  Pill,
  Spinner,
  TextArea,
  TextInput,
} from '../ui/primitives';

export function StepParties({
  role,
  title,
  description,
  hint,
  minCount,
}: {
  role: PartyRole;
  title: string;
  description: string;
  hint: string;
  minCount: number;
}) {
  const qc = useQueryClient();
  const { data: parties, isLoading } = useQuery<Party[]>({
    queryKey: ['parties', role],
    queryFn: () => api.get<Party[]>(`/api/parties?role=${role}`),
  });
  const [displayName, setDisplayName] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const createMut = useMutation({
    // allowSimilar: one-time setup adds many names in a row; skip the
    // Phase-4c similar-name prompt here to keep setup smooth.
    mutationFn: (body: { displayName: string; notes?: string | null }) =>
      api.post<Party>('/api/parties', { role, ...body, allowSimilar: true }),
    onSuccess: () => {
      setDisplayName('');
      setNotes('');
      setError(null);
      void qc.invalidateQueries();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/parties/${id}`),
    onSuccess: () => void qc.invalidateQueries(),
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  const count = parties?.length ?? 0;

  return (
    <div className="space-y-4">
      <ActionQueueCard
        section="accounts"
        eyebrow={role === 'upline' ? 'Import sources' : 'People using accounts'}
        title={count >= minCount ? `${count} ${role} entr${count === 1 ? 'y' : 'ies'} ready` : `Add at least ${minCount} ${role}${minCount === 1 ? '' : 's'}`}
        description={description}
        tone={count >= minCount ? 'accent' : 'warn'}
        meta={<div className="text-[12px] leading-5 text-ink-muted">{hint}</div>}
      />

      <SectionCard section="accounts" title={title}>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            const name = displayName.trim();
            if (!name) {
              setError('Display name is required.');
              return;
            }
            createMut.mutate({ displayName: name, notes: notes.trim() || null });
          }}
        >
          <TextInput
            label="Display name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            name="displayName"
            placeholder={role === 'upline' ? 'e.g. SRB' : 'e.g. Gori Bhaiya'}
            hint={hint}
          />
          <TextArea label="Notes (optional)" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          {error && <ErrorBanner>{error}</ErrorBanner>}
          <Button type="submit" disabled={createMut.isPending}>
            {createMut.isPending ? <Spinner /> : `Add ${role}`}
          </Button>
        </form>
      </SectionCard>

      <SectionCard section="accounts" title="Existing entries">
        <div className="mb-3 flex items-center gap-2">
          <Pill tone={count >= minCount ? 'green' : 'amber'}>
            {count}/{minCount} minimum
          </Pill>
        </div>
        {isLoading ? (
          <div className="flex items-center gap-2 text-small text-ink-muted">
            <Spinner /> loading...
          </div>
        ) : parties && parties.length > 0 ? (
          <div className="space-y-3">
            {parties.map((party) => (
              <div
                key={party.id}
                className="flex flex-wrap items-start justify-between gap-3 rounded-[22px] border border-border/70 bg-surface px-4 py-3 shadow-sm backdrop-blur"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-small font-semibold tracking-[-0.01em] text-ink">{party.displayName}</div>
                  <div className="mt-1 text-[12px] leading-5 text-ink-muted">{party.notes?.trim() || 'No notes saved.'}</div>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    if (confirm(`Delete ${party.displayName}?`)) deleteMut.mutate(party.id);
                  }}
                >
                  Delete
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-caption text-ink-muted">No {role} parties yet.</p>
        )}
      </SectionCard>
    </div>
  );
}
